import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, AlertTriangle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { btnP, btnS, fmtL } from "./BpKit";

const Preview = ({ pv }) => (
  <div className="space-y-4 text-sm" data-testid="bp-import-preview">
    <p>{pv.lines} account lines found. {pv.linked} matched an account in Axiom One{pv.unlinked ? `; ${pv.unlinked} didn't and will load as "not linked" rows the person can link later` : ""}.</p>
    <div className="overflow-x-auto border border-slate-200 rounded-sm">
      <table className="ax-table w-full" data-testid="bp-import-people-table">
        <thead><tr><th>Person</th><th className="text-right">Accounts</th><th className="text-right">FY plan</th><th className="text-right">With weekly plan</th><th>Weekly targets</th><th>Status</th></tr></thead>
        <tbody>
          {pv.people.map((x) => (
            <tr key={x.user_id} data-testid={`bp-import-person-${x.user_id}`}>
              <td className="font-medium">{x.name}</td><td className="text-right num">{x.rows}</td><td className="text-right num">{fmtL(x.total)}</td><td className="text-right num">{x.weekly_rows}</td>
              <td className="text-xs">{x.targets.join(", ") || "—"}</td>
              <td className="text-xs">{x.will_skip ? <span className="text-amber-700">Skipped: plan is {x.status}</span> : ["draft", "returned"].includes(x.status) ? <span className="text-amber-700">Replaces their current draft</span> : "Loads as draft"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {pv.unmatched.length > 0 && (
      <div className="flex gap-2 border border-amber-300 bg-amber-50 rounded-sm p-3 text-amber-800" data-testid="bp-import-unmatched">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>No login found for: {pv.unmatched.map((u) => `${u.owner} (${u.rows} accounts, ${fmtL(u.total)})`).join(", ")}. Their rows still count in the branch plans. Give them a login with the same name, then load the sheet again to fill their personal plans.</span>
      </div>
    )}
    <p className="text-xs text-slate-600">Branch plans: {pv.branches.map((b) => `${b.label} ${fmtL(b.total)}`).join(" · ")}. {pv.team_targets.length ? `Team weekly targets: ${pv.team_targets.join(", ")}.` : ""} Plans already submitted or approved are left alone.</p>
  </div>
);

export const BpImport = ({ fy, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const pick = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("fy", fy);
    setBusy(true);
    api.post("/bp/import/preview", fd).then((r) => setPv(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  const commit = () => {
    setBusy(true);
    api.post(`/bp/import/${pv.import_id}/commit`).then((r) => {
      toast.success(`Loaded as drafts: ${r.data.loaded.join(", ") || "nothing new"}`);
      if (r.data.skipped.length) toast.info(`Left alone (already submitted or approved): ${r.data.skipped.join(", ")}`);
      setOpen(false); setPv(null); onDone();
    }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <>
      <button type="button" data-testid="bp-import-open-button" className={`${btnS} inline-flex items-center`} onClick={() => setOpen(true)}><FileSpreadsheet className="h-4 w-4 mr-1" />Load from BP sheet</button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPv(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bp-import-dialog">
          <DialogHeader>
            <DialogTitle>Load FY {fy} plans from the BP sheet</DialogTitle>
            <DialogDescription>Upload the Business Plan workbook (.xlsx). Axiom One reads the MONTHLY, WEEKLY, WINNING SHEET and ANNUAL tabs, shows you what it found, and loads everything as drafts for each person to check and submit.</DialogDescription>
          </DialogHeader>
          {!pv ? (
            <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-sm p-8 cursor-pointer transition-colors ${busy ? "border-slate-200 text-slate-400" : "border-slate-300 hover:border-primary"}`} data-testid="bp-import-drop">
              <FileSpreadsheet className="h-8 w-8 text-primary" />
              <span className="text-sm font-medium">{busy ? "Reading the workbook…" : "Choose the BP workbook"}</span>
              <input type="file" accept=".xlsx" className="hidden" disabled={busy} data-testid="bp-import-file-input" onChange={(e) => pick(e.target.files?.[0])} />
            </label>
          ) : <Preview pv={pv} />}
          {pv && (
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className={btnS} onClick={() => setPv(null)} data-testid="bp-import-back-button">Choose another file</button>
              <button type="button" className={btnP} disabled={busy} onClick={commit} data-testid="bp-import-commit-button">Load as drafts</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
